// Static interface copy in every supported app language. User and model content is not translated here.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  title: 'You’re now signed in as {account} at {company} in another tab.',
  titleNoCompany: 'You’re now signed in as {account} in another tab.',
  titleSomeone: 'You’re now signed in as a different account in another tab.',
  titleSignedOut: 'You were signed out in another tab.',
  lead: 'Nothing from that account is shown here. Reload to continue.',
  reload: 'Reload',
  signInAs: 'Sign in as {previous}',
  signInAsPrevious: 'Sign in again',
};

export type SessionGuardCopyKey = keyof typeof en;

export const sessionGuardCopy: NamespaceCatalog<keyof typeof en> = {
  en: {
    title: 'You’re now signed in as {account} at {company} in another tab.',
    titleNoCompany: 'You’re now signed in as {account} in another tab.',
    titleSomeone: 'You’re now signed in as a different account in another tab.',
    titleSignedOut: 'You were signed out in another tab.',
    lead: 'Nothing from that account is shown here. Reload to continue.',
    reload: 'Reload',
    signInAs: 'Sign in as {previous}',
    signInAsPrevious: 'Sign in again',
  },
  bg: {
    title: 'Вече сте влезли като {account} в {company} в друг раздел.',
    titleNoCompany: 'Вече сте влезли като {account} в друг раздел.',
    titleSomeone: 'Вече сте влезли с друг акаунт в друг раздел.',
    titleSignedOut: 'Излязохте от профила си в друг раздел.',
    lead: 'Тук не се показва нищо от този акаунт. Презаредете, за да продължите.',
    reload: 'Презареди',
    signInAs: 'Влезте като {previous}',
    signInAsPrevious: 'Влезте отново',
  },
  cs: {
    title: 'V jiném panelu jste se právě přihlásili jako {account} ve společnosti {company}.',
    titleNoCompany: 'V jiném panelu jste se právě přihlásili jako {account}.',
    titleSomeone: 'V jiném panelu jste se právě přihlásili jako jiný účet.',
    titleSignedOut: 'V jiném panelu jste byli odhlášeni.',
    lead: 'Z tohoto účtu se zde nic nezobrazuje. Pokračujte opětovným načtením.',
    reload: 'Načíst znovu',
    signInAs: 'Přihlásit se jako {previous}',
    signInAsPrevious: 'Přihlásit se znovu',
  },
  da: {
    title: 'Du er nu logget ind som {account} hos {company} i en anden fane.',
    titleNoCompany: 'Du er nu logget ind som {account} i en anden fane.',
    titleSomeone: 'Du er nu logget ind med en anden konto i en anden fane.',
    titleSignedOut: 'Du blev logget ud i en anden fane.',
    lead: 'Der vises ikke noget fra den konto her. Genindlæs for at fortsætte.',
    reload: 'Genindlæs',
    signInAs: 'Log ind som {previous}',
    signInAsPrevious: 'Log ind igen',
  },
  de: {
    title: 'Sie sind jetzt in einem anderen Tab als {account} bei {company} angemeldet.',
    titleNoCompany: 'Sie sind jetzt in einem anderen Tab als {account} angemeldet.',
    titleSomeone: 'Sie sind jetzt in einem anderen Tab mit einem anderen Konto angemeldet.',
    titleSignedOut: 'Sie wurden in einem anderen Tab abgemeldet.',
    lead: 'Von diesem Konto wird hier nichts angezeigt. Laden Sie die Seite neu, um fortzufahren.',
    reload: 'Neu laden',
    signInAs: 'Als {previous} anmelden',
    signInAsPrevious: 'Erneut anmelden',
  },
  el: {
    title: 'Συνδεθήκατε ως {account} στην {company} σε άλλη καρτέλα.',
    titleNoCompany: 'Συνδεθήκατε ως {account} σε άλλη καρτέλα.',
    titleSomeone: 'Συνδεθήκατε ως διαφορετικός λογαριασμός σε άλλη καρτέλα.',
    titleSignedOut: 'Αποσυνδεθήκατε σε άλλη καρτέλα.',
    lead: 'Δεν εμφανίζεται εδώ τίποτα από αυτόν τον λογαριασμό. Κάντε επαναφόρτωση για να συνεχίσετε.',
    reload: 'Επαναφόρτωση',
    signInAs: 'Σύνδεση ως {previous}',
    signInAsPrevious: 'Σύνδεση ξανά',
  },
  es: {
    title: 'Has iniciado sesión como {account} en {company} en otra pestaña.',
    titleNoCompany: 'Has iniciado sesión como {account} en otra pestaña.',
    titleSomeone: 'Has iniciado sesión con otra cuenta en otra pestaña.',
    titleSignedOut: 'Has cerrado sesión en otra pestaña.',
    lead: 'Aquí no se muestra nada de esa cuenta. Recarga para continuar.',
    reload: 'Recargar',
    signInAs: 'Iniciar sesión como {previous}',
    signInAsPrevious: 'Iniciar sesión de nuevo',
  },
  et: {
    title: 'Logisid teises vahekaardis kontoga {account} ettevõttes {company} sisse.',
    titleNoCompany: 'Logisid teises vahekaardis kontoga {account} sisse.',
    titleSomeone: 'Logisid teises vahekaardis sisse teise kontoga.',
    titleSignedOut: 'Sind logiti teises vahekaardis välja.',
    lead: 'Selle konto andmeid siin ei kuvata. Jätkamiseks laadi leht uuesti.',
    reload: 'Laadi uuesti',
    signInAs: 'Logi sisse kontoga {previous}',
    signInAsPrevious: 'Logi uuesti sisse',
  },
  fi: {
    title: 'Olet nyt kirjautunut tilille {account} yrityksessä {company} toisella välilehdellä.',
    titleNoCompany: 'Olet nyt kirjautunut tilille {account} toisella välilehdellä.',
    titleSomeone: 'Olet nyt kirjautunut eri tilille toisella välilehdellä.',
    titleSignedOut: 'Sinut kirjattiin ulos toisella välilehdellä.',
    lead: 'Tämän tilin tietoja ei näytetä täällä. Jatka lataamalla sivu uudelleen.',
    reload: 'Lataa uudelleen',
    signInAs: 'Kirjaudu tilille {previous}',
    signInAsPrevious: 'Kirjaudu uudelleen',
  },
  fr: {
    title:
      'Vous êtes maintenant connecté·e en tant que {account} chez {company} dans un autre onglet.',
    titleNoCompany: 'Vous êtes maintenant connecté·e en tant que {account} dans un autre onglet.',
    titleSomeone: 'Vous êtes maintenant connecté·e avec un autre compte dans un autre onglet.',
    titleSignedOut: 'Vous avez été déconnecté·e dans un autre onglet.',
    lead: 'Rien de ce compte ne s’affiche ici. Rechargez pour continuer.',
    reload: 'Recharger',
    signInAs: 'Se connecter en tant que {previous}',
    signInAsPrevious: 'Se reconnecter',
  },
  ga: {
    title: 'Tá tú sínithe isteach anois mar {account} ag {company} i gcluaisín eile.',
    titleNoCompany: 'Tá tú sínithe isteach anois mar {account} i gcluaisín eile.',
    titleSomeone: 'Tá tú sínithe isteach anois mar chuntas eile i gcluaisín eile.',
    titleSignedOut: 'Síníodh amach thú i gcluaisín eile.',
    lead: 'Ní thaispeántar aon rud ón gcuntas sin anseo. Athlódáil chun leanúint ar aghaidh.',
    reload: 'Athlódáil',
    signInAs: 'Sínigh isteach mar {previous}',
    signInAsPrevious: 'Sínigh isteach arís',
  },
  hr: {
    title: 'Sada ste prijavljeni kao {account} u tvrtki {company} na drugoj kartici.',
    titleNoCompany: 'Sada ste prijavljeni kao {account} na drugoj kartici.',
    titleSomeone: 'Sada ste prijavljeni s drugim računom na drugoj kartici.',
    titleSignedOut: 'Odjavljeni ste na drugoj kartici.',
    lead: 'Ovdje se ne prikazuje ništa s tog računa. Ponovno učitajte stranicu za nastavak.',
    reload: 'Ponovno učitaj',
    signInAs: 'Prijavi se kao {previous}',
    signInAsPrevious: 'Ponovno se prijavi',
  },
  hu: {
    title: 'Egy másik lapon most {account} fiókkal van bejelentkezve a(z) {company} vállalatnál.',
    titleNoCompany: 'Egy másik lapon most {account} fiókkal van bejelentkezve.',
    titleSomeone: 'Egy másik lapon most egy másik fiókkal van bejelentkezve.',
    titleSignedOut: 'Egy másik lapon kijelentkeztették.',
    lead: 'Az adott fiókból itt semmi sem jelenik meg. A folytatáshoz töltsd újra az oldalt.',
    reload: 'Újratöltés',
    signInAs: 'Bejelentkezés mint {previous}',
    signInAsPrevious: 'Bejelentkezés újra',
  },
  it: {
    title: 'Hai effettuato l’accesso come {account} presso {company} in un’altra scheda.',
    titleNoCompany: 'Hai effettuato l’accesso come {account} in un’altra scheda.',
    titleSomeone: 'Hai effettuato l’accesso con un altro account in un’altra scheda.',
    titleSignedOut: 'Hai effettuato la disconnessione in un’altra scheda.',
    lead: 'Qui non viene mostrato nulla di quell’account. Ricarica per continuare.',
    reload: 'Ricarica',
    signInAs: 'Accedi come {previous}',
    signInAsPrevious: 'Accedi di nuovo',
  },
  lt: {
    title: 'Kitame skirtuke prisijungėte kaip {account} prie {company}.',
    titleNoCompany: 'Kitame skirtuke prisijungėte kaip {account}.',
    titleSomeone: 'Kitame skirtuke prisijungėte naudodami kitą paskyrą.',
    titleSignedOut: 'Kitame skirtuke nuo paskyros atsijungta.',
    lead: 'Čia nieko iš tos paskyros nerodoma. Įkelkite iš naujo, kad tęstumėte.',
    reload: 'Įkelti iš naujo',
    signInAs: 'Prisijungti kaip {previous}',
    signInAsPrevious: 'Prisijungti dar kartą',
  },
  lv: {
    title: 'Tagad citā cilnē esat pierakstījies kā {account} uzņēmumā {company}.',
    titleNoCompany: 'Tagad citā cilnē esat pierakstījies kā {account}.',
    titleSomeone: 'Tagad citā cilnē esat pierakstījies ar citu kontu.',
    titleSignedOut: 'Citā cilnē no jūsu konta izrakstījās.',
    lead: 'Šeit nekas no šī konta netiek rādīts. Lai turpinātu, atkārtoti ielādējiet lapu.',
    reload: 'Atkārtoti ielādēt',
    signInAs: 'Pierakstīties kā {previous}',
    signInAsPrevious: 'Pierakstīties vēlreiz',
  },
  mt: {
    title: "Issa dħalt bħala {account} għand {company} f'tab oħra.",
    titleNoCompany: "Issa dħalt bħala {account} f'tab oħra.",
    titleSomeone: "Issa dħalt bħala kont differenti f'tab oħra.",
    titleSignedOut: "Ħriġt mill-kont f'tab oħra.",
    lead: "Xejn minn dak il-kont mhu muri hawn. Erġa' għabbi biex tkompli.",
    reload: "Erġa' għabbi",
    signInAs: 'Idħol bħala {previous}',
    signInAsPrevious: "Erġa' idħol",
  },
  nl: {
    title: 'Je bent nu in een ander tabblad ingelogd als {account} bij {company}.',
    titleNoCompany: 'Je bent nu in een ander tabblad ingelogd als {account}.',
    titleSomeone: 'Je bent nu in een ander tabblad ingelogd met een ander account.',
    titleSignedOut: 'Je bent in een ander tabblad uitgelogd.',
    lead: 'Er wordt hier niets van dat account weergegeven. Laad de pagina opnieuw om door te gaan.',
    reload: 'Opnieuw laden',
    signInAs: 'Inloggen als {previous}',
    signInAsPrevious: 'Opnieuw inloggen',
  },
  pl: {
    title: 'Zalogowano jako {account} w firmie {company} na innej karcie.',
    titleNoCompany: 'Zalogowano jako {account} na innej karcie.',
    titleSomeone: 'Na innej karcie zalogowano inne konto.',
    titleSignedOut: 'Wylogowano Cię na innej karcie.',
    lead: 'Nie pokazujemy tutaj niczego z tego konta. Odśwież stronę, aby kontynuować.',
    reload: 'Odśwież',
    signInAs: 'Zaloguj się jako {previous}',
    signInAsPrevious: 'Zaloguj się ponownie',
  },
  pt: {
    title: 'Você entrou como {account} em {company} em outra aba.',
    titleNoCompany: 'Você entrou como {account} em outra aba.',
    titleSomeone: 'Você entrou com outra conta em outra aba.',
    titleSignedOut: 'Sua sessão foi encerrada em outra aba.',
    lead: 'Nada dessa conta é mostrado aqui. Recarregue para continuar.',
    reload: 'Recarregar',
    signInAs: 'Entrar como {previous}',
    signInAsPrevious: 'Entrar novamente',
  },
  ro: {
    title: 'Acum ești conectat(ă) ca {account} la {company} într-o altă filă.',
    titleNoCompany: 'Acum ești conectat(ă) ca {account} într-o altă filă.',
    titleSomeone: 'Acum ești conectat(ă) cu un alt cont într-o altă filă.',
    titleSignedOut: 'Ai fost deconectat(ă) într-o altă filă.',
    lead: 'Aici nu este afișat nimic din acel cont. Reîncarcă pentru a continua.',
    reload: 'Reîncarcă',
    signInAs: 'Conectează-te ca {previous}',
    signInAsPrevious: 'Conectează-te din nou',
  },
  sk: {
    title: 'Teraz ste prihlásení ako {account} v spoločnosti {company} na inej karte.',
    titleNoCompany: 'Teraz ste prihlásení ako {account} na inej karte.',
    titleSomeone: 'Teraz ste prihlásení ako iný účet na inej karte.',
    titleSignedOut: 'Na inej karte ste boli odhlásení.',
    lead: 'Z tohto účtu sa tu nič nezobrazuje. Ak chcete pokračovať, znova načítajte stránku.',
    reload: 'Znova načítať',
    signInAs: 'Prihlásiť sa ako {previous}',
    signInAsPrevious: 'Znova sa prihlásiť',
  },
  sl: {
    title: 'Zdaj ste kot {account} prijavljeni v {company} v drugem zavihku.',
    titleNoCompany: 'Zdaj ste kot {account} prijavljeni v drugem zavihku.',
    titleSomeone: 'V drugem zavihku ste se prijavili z drugim računom.',
    titleSignedOut: 'V drugem zavihku ste se odjavili.',
    lead: 'Tukaj ni prikazano nič iz tega računa. Za nadaljevanje znova naložite stran.',
    reload: 'Znova naloži',
    signInAs: 'Prijavi se kot {previous}',
    signInAsPrevious: 'Znova se prijavi',
  },
  sv: {
    title: 'Du är nu inloggad som {account} hos {company} i en annan flik.',
    titleNoCompany: 'Du är nu inloggad som {account} i en annan flik.',
    titleSomeone: 'Du är nu inloggad med ett annat konto i en annan flik.',
    titleSignedOut: 'Du loggades ut i en annan flik.',
    lead: 'Inget från det kontot visas här. Ladda om för att fortsätta.',
    reload: 'Ladda om',
    signInAs: 'Logga in som {previous}',
    signInAsPrevious: 'Logga in igen',
  },
};
export default sessionGuardCopy;
